import { DriversFilter, DriverApi } from '../models';
import { Authorize, Table } from '@/components';
import { BUSINESS_ROLE_GROUPS } from '@/components/security/AppRoles';
import { useSortableData } from '@/hooks';
import { useDrivers, useDriversTable } from '../hooks';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';
import {
    Edit as EditIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import ConfirmDriverStatusModal from './ConfirmDriverStatusModal';

type DriversTableProps = {
    driversFilter?: DriversFilter;
    currentPage: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
};

const DriversTable = ({
    driversFilter,
    currentPage,
    pageSize,
    onPageChange,
    onPageSizeChange,
}: DriversTableProps) => {
    const navigate = useNavigate();

    const { drivers, isDriversLoading, totalItems, totalPages } = useDrivers({
        paginated: {
            page: currentPage,
            pageSize: pageSize,
        },
        filters: driversFilter,
    });

    const {
        columns,
        modalState,
        isEnabling,
        handleCloseModal,
        handleConfirm,
    } = useDriversTable(drivers);

    const { sortedData, selectedSort, handleSort } = useSortableData({
        data: drivers,
        currentPage,
    });

    const renderActions = (rowData: Record<string, unknown>) => {
        const driver = rowData as unknown as DriverApi;
        if (!driver?.id) return null;
        return (
            <div className='flex items-center gap-0.5'>
                <IconButton
                    aria-label='Ver conductor'
                    onClick={() => navigate(`/business/drivers/${driver.id}`)}
                >
                    <VisibilityIcon />
                </IconButton>
                <Authorize
                    permission={BUSINESS_ROLE_GROUPS.adminOrSupervisor}
                    fallback={null}
                >
                    <IconButton
                        aria-label='Editar conductor'
                        onClick={() =>
                            navigate(`/business/drivers/${driver.id}/edit`)
                        }
                    >
                        <EditIcon />
                    </IconButton>
                </Authorize>
            </div>
        );
    };

    return (
        <>
            <div className='w-full p-5 flex flex-col bg-white rounded-lg shadow-md gap-5'>
                <Table
                    columns={columns}
                    data={sortedData}
                    renderActions={renderActions}
                    isLoading={isDriversLoading}
                    selectedSort={selectedSort}
                    handleSort={handleSort}
                    pagination={{
                        totalItems,
                        totalPages,
                        currentPage,
                        rowsPerPage: pageSize,
                        onPageChange,
                        onRowsPerPageChange: onPageSizeChange,
                    }}
                />
            </div>
            <ConfirmDriverStatusModal
                open={modalState.open}
                isActivating={modalState.isActivating}
                driverName={modalState.driverName}
                isLoading={isEnabling}
                onClose={handleCloseModal}
                onConfirm={handleConfirm}
            />
        </>
    );
};

export default DriversTable;
